/**
 * Converts estimated start time to seconds
 * Supports formats like: "2m", "3m 30s", "90s"
 *
 * @param time - The time value string to parse
 * @returns The time in seconds, or undefined if invalid
 *
 * @example
 * parseTimeToSeconds("2m") // returns 120
 * parseTimeToSeconds("3m 30s") // returns 210
 * parseTimeToSeconds("90s") // returns 90
 */
export const parseTimeToSeconds = (
  time: string | number
): number | undefined => {
  // Handle invalid inputs
  if (time === undefined || time === null) return undefined;

  // Convert to string to safely handle numbers
  const timeStr = String(time);

  // Handle empty strings
  if (timeStr.trim() === "") return undefined;

  // Parse string formats like "2m", "3m 30s", "90s"
  let totalSeconds = 0;
  let foundMatch = false;

  // Match minutes
  const minutesMatch = timeStr.match(/(\d+)m/);
  if (minutesMatch) {
    totalSeconds += parseInt(minutesMatch[1]) * 60;
    foundMatch = true;
  }

  // Match seconds
  const secondsMatch = timeStr.match(/(\d+)s/);
  if (secondsMatch) {
    totalSeconds += parseInt(secondsMatch[1]);
    foundMatch = true;
  }

  // If no matches found, try parsing as plain number
  if (!foundMatch && !isNaN(Number(timeStr))) {
    totalSeconds = Number(timeStr);
    foundMatch = true;
  }

  return foundMatch ? totalSeconds : undefined;
};

/**
 * Formats seconds as a human-readable time string, rounded up to the nearest half minute.
 *
 * @param seconds - The time in seconds to format
 * @returns The formatted time rounded up to the nearest half minute, or undefined if invalid
 *
 * @example
 * formatSeconds(120) // "2 minutes"
 * formatSeconds(210) // "3.5 minutes"
 * formatSeconds(215) // "4 minutes"
 * formatSeconds(90) // "1.5 minutes"
 * formatSeconds(15) // "30 seconds"
 * formatSeconds(undefined) // undefined
 */
export const formatSeconds = (
  seconds: number | undefined
): string | undefined => {
  // If invalid time, return undefined
  if (seconds === undefined || seconds === null) return undefined;

  // Round up to nearest 30 seconds (0.5 minutes)
  const roundedSeconds = Math.ceil(seconds / 30) * 30;

  // If less than 60 seconds, format as seconds
  if (roundedSeconds < 60) {
    return `${roundedSeconds} seconds`;
  }

  // Convert to minutes
  const minutes = roundedSeconds / 60;

  // Format as minutes
  if (minutes === Math.floor(minutes)) {
    // Whole number
    return `${minutes} ${minutes === 1 ? "minute" : "minutes"}`;
  } else {
    // Has decimal (always .5 due to rounding)
    return `${minutes} minutes`;
  }
};
